import React from 'react';
import PropTypes from 'prop-types';
import { Button, message, Popconfirm, Tooltip } from 'antd';
import useSaveEditor from './data/useSaveEditor';
import { useUserReviewEditor } from './data/UserReviewEditorProvider';
import { RatingType } from './data/RatingType';
import { withIsMobile } from 'treeline-react-core-components/WindowSizeProvider';

EditorControlButtons.propTypes = {
	isMobile: PropTypes.func,
	productName: PropTypes.string,
	editorController: PropTypes.object,
	drawer: PropTypes.shape({ open: PropTypes.func, close: PropTypes.func }),
};

function EditorControlButtons({
	drawer,
	isMobile,
	productName,
	editorController,
}) {
	const [isRemoving, setIsRemoving] = React.useState(false);

	const { isLoading, handleSave } = useSaveEditor(editorController);

	const { ratingsEditor, shareTargetsEditor } = useUserReviewEditor();

	const handleRemove = async () => {
		setIsRemoving(true);
		await editorController.remove();
		drawer.close();
		message.success(`Deleted review for ${productName}`);
	};

	const renderCancelButton = () => {
		return (
			<Button
				onClick={drawer.close}
				disabled={isRemoving || isLoading}
				style={{ minWidth: 80 }}>
				Cancel
			</Button>
		);
	};

	const editorCanBeSaved = () => {
		const { getGenreCommunityOptionsState } = shareTargetsEditor;
		return (
			!isRemoving &&
			ratingsEditor.exists(RatingType.overall) &&
			!getGenreCommunityOptionsState().isLoading
		);
	};

	const getCannotSaveEditorMessage = () => {
		const { getGenreCommunityOptionsState } = shareTargetsEditor;

		if (isRemoving) {
			return 'Deleting...';
		}

		if (!ratingsEditor.exists(RatingType.overall)) {
			return 'Overall rating is required';
		}

		if (getGenreCommunityOptionsState().isLoading) {
			return 'Loading genre community options';
		}

		return 'Cannot save';
	};

	const handleRenderSaveButton = canSaveEditor => {
		const renderSaveButton = () => {
			return (
				<Button
					type="primary"
					disabled={!canSaveEditor}
					loading={isLoading}
					style={{ marginLeft: '1.5rem', minWidth: 80 }}
					onClick={handleSave}>
					Save
				</Button>
			);
		};

		if (isMobile()) {
			return renderSaveButton();
		}
		return (
			<Tooltip
				title={
					canSaveEditor
						? 'Save review and ratings'
						: getCannotSaveEditorMessage()
				}>
				{renderSaveButton()}
			</Tooltip>
		);
	};

	const canSaveEditor = editorCanBeSaved();

	return (
		<div
			style={{
				display: 'flex',
				marginBottom: '5rem',
				justifyContent: 'space-between',
			}}>
			<div>
				{editorController.doesReviewExist() && renderCancelButton()}
			</div>
			<div style={{ display: 'flex' }}>
				{editorController.doesReviewExist() ? (
					<Popconfirm
						okText="Yes"
						cancelText="No"
						title="Do you want to delete the review?"
						placement="topRight"
						onConfirm={handleRemove}>
						<Button
							type="danger"
							disabled={isLoading}
							loading={isRemoving}
							style={{ minWidth: 80 }}>
							Delete
						</Button>
					</Popconfirm>
				) : (
					renderCancelButton()
				)}
				{handleRenderSaveButton(canSaveEditor)}
			</div>
		</div>
	);
}

export default withIsMobile(EditorControlButtons);
